import { useMemo } from 'react';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { TooltipComponent, GridComponent, LegendComponent, RadarComponent } from 'echarts/components';
import { BarChart, PieChart, RadarChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import { Card, Col, Row } from 'react-bootstrap';
import { useAppContext } from '../../../providers/AppProvider';
import {
  getAttackCategoryPieOption,
  getBlastRadiusRadarOption,
  getComplianceWaterfallOption,
  getEvidenceConfidenceOption,
  getImpactSeverityPieOption,
  getRiskFactorDistributionOption,
  getRootCauseDistributionOption,
  getSeverityDistributionOption
} from './pdcCharts.utils';
import type { PDCRun } from './pdc.utils';
import '../Bias/BiasDetectionChartsSection.css';

echarts.use([
  TooltipComponent,
  GridComponent,
  LegendComponent,
  RadarComponent,
  BarChart,
  PieChart,
  RadarChart,
  CanvasRenderer
]);

function ChartCard({
  title,
  subtitle,
  option
}: {
  title: string;
  subtitle: string;
  option: ReturnType<typeof getRiskFactorDistributionOption>;
}) {
  if (!option) {
    return (
      <Card className="border-0 shadow-sm h-100">
        <Card.Body>
          <h6 className="mb-1">{title}</h6>
          <p className="small text-muted mb-2">{subtitle}</p>
          <p className="text-muted small mb-0">No data available for this chart.</p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow-sm h-100">
      <Card.Body>
        <h6 className="mb-1">{title}</h6>
        <p className="small text-muted mb-2">{subtitle}</p>
        <ReactEChartsCore echarts={echarts} option={option} style={{ height: 320 }} />
      </Card.Body>
    </Card>
  );
}

export function PDCCharts({ run }: { run: PDCRun | null | undefined }) {
  const { getThemeColor } = useAppContext();
  const resolvedRun = run ?? null;

  const colors = useMemo(
    () => ({
      dangerColor: getThemeColor('danger'),
      primaryColor: getThemeColor('primary'),
      bodyHighlightBg: getThemeColor('body-highlight-bg'),
      borderColor: getThemeColor('border-color'),
      lightTextEmphasis: getThemeColor('light-text-emphasis')
    }),
    [getThemeColor]
  );

  const charts = useMemo(
    () => [
      {
        id: 'risk-factors',
        title: 'Risk Factor Distribution',
        subtitle: 'Prompt security vectors driving overall risk.',
        option: getRiskFactorDistributionOption(resolvedRun, colors)
      },
      {
        id: 'attack-category',
        title: 'Attack Category Distribution',
        subtitle: 'Dominant attack types across findings.',
        option: getAttackCategoryPieOption(resolvedRun, colors)
      },
      {
        id: 'root-cause',
        title: 'Root Cause Distribution',
        subtitle: 'Vulnerabilities behind identified prompt attacks.',
        option: getRootCauseDistributionOption(resolvedRun, colors)
      },
      {
        id: 'evidence-confidence',
        title: 'Evidence Confidence',
        subtitle: 'Confidence level per finding.',
        option: getEvidenceConfidenceOption(resolvedRun, colors)
      },
      {
        id: 'blast-radius',
        title: 'Blast Radius Impact Profile',
        subtitle: 'Estimated impact across key business areas.',
        option: getBlastRadiusRadarOption(resolvedRun, colors)
      },
      {
        id: 'impact-severity',
        title: 'Impact Severity Distribution',
        subtitle: 'Breakdown of blast radius severity levels.',
        option: getImpactSeverityPieOption(resolvedRun, colors)
      },
      {
        id: 'severity-counts',
        title: 'Severity Distribution',
        subtitle: 'Findings count by severity level.',
        option: getSeverityDistributionOption(resolvedRun, colors)
      },
      {
        id: 'waterfall',
        title: 'Compliance Waterfall',
        subtitle: 'Penalties deducted from base score to final defense score.',
        option: getComplianceWaterfallOption(resolvedRun, colors)
      }
    ],
    [resolvedRun, colors]
  );

  if (!resolvedRun) {
    return <p className="text-muted small mb-0">No Prompt Injection chart data available.</p>;
  }

  return (
    <div className="mt-3" aria-label="PDC charts">
      <Row className="g-3">
        {charts.map(chart => (
          <Col key={chart.id} xs={12} lg={6}>
            <ChartCard title={chart.title} subtitle={chart.subtitle} option={chart.option} />
          </Col>
        ))}
      </Row>
    </div>
  );
}
